/**
 * Ordered registry of onboarding steps.
 *
 * Step 1 is the home-screen welcome dialog (`OnboardingWelcomeDialog`).
 * Steps 2–10 are delivered through this in-canvas widget.
 *
 * To add a new step:
 *  1. Create `./MyNewStep.tsx` conforming to `StepProps`.
 *  2. Append it here with an `anchor` that keeps the relevant canvas /
 *     chrome area unobstructed.
 *
 * The orchestrator routes by 1-based index read from Loro's `data.step`.
 * The last step's `onAdvance` falls through to `onFinish` automatically.
 */

import type { StepDefinition } from "../shared/types";
import { DeckPublishedStep } from "./DeckPublishedStep";
import { DeckWorkingStep } from "./DeckWorkingStep";
import { DragBriefStep } from "./DragBriefStep";
import { DragMarkStep } from "./DragMarkStep";
import { GenerateDeckStep } from "./GenerateDeckStep";
import { GenerateImagesStep } from "./GenerateImagesStep";
import { ImagesWorkingStep } from "./ImagesWorkingStep";
import { MakeItYoursStep } from "./MakeItYoursStep";
import { OpenDeckPublishStep } from "./OpenDeckPublishStep";
import { SelectBriefStep } from "./SelectBriefStep";

export const ONBOARDING_STEPS: readonly StepDefinition[] = [
  // Step 2: Tour kickoff — centered compact card matches the home welcome
  // dialog's visual weight so the first canvas moment feels continuous.
  { key: "drag-mark", component: DragMarkStep, anchor: "centerCompact" },

  // Step 3: Drag the AeroCarry brief. Right-anchored so the canvas drop
  // area stays open to the left.
  { key: "drag-brief", component: DragBriefStep, anchor: "rightCenterCompact" },

  // Step 4: Select the brief. Top-left — the cursor demo + node hover
  // lift point the user at the brief, so the card tucks into the
  // corner out of the canvas click path. Wide variant pushes it
  // further off the left rail.
  {
    key: "select-brief",
    component: SelectBriefStep,
    anchor: "topLeftCompactWide",
  },

  // Step 5: Open chat + send the generate-images prompt. Top-left leaves
  // the chat panel (right side) unobstructed. Wide variant pushes it
  // further off the left rail.
  {
    key: "generate-images",
    component: GenerateImagesStep,
    anchor: "topLeftCompactWide",
  },

  // Step 6: Watch the images land. Top-left keeps the card up out of
  // the canvas area where the image column appears to the right of
  // the PDF. Wide variant pushes it further off the left rail.
  {
    key: "images-working",
    component: ImagesWorkingStep,
    anchor: "topLeftCompactWide",
  },

  // Step 7: Multi-select + send the deck prompt. Top-right keeps the
  // image column and the canvas multi-select area visible.
  {
    key: "generate-deck",
    component: GenerateDeckStep,
    anchor: "topRightCompact",
  },

  // Step 8: Deck lands → prompt to double-click and open it. Merges
  // the former separate "double-click to open" beat so the user moves
  // directly from "your deck is ready" to "click to open" without an
  // extra coachmark hop.
  {
    key: "deck-working",
    component: DeckWorkingStep,
    anchor: "topLeftCompact",
  },

  // Step 9: Publish the deck. Mounts with the deck pane already open
  // (step 8 advanced on that open). Top-left keeps the right pane
  // (where the detail view sits) and the deck node visible.
  {
    key: "open-deck-publish",
    component: OpenDeckPublishStep,
    anchor: "topLeftCompact",
  },

  // Step 9b: Your deck is live — shows the published URL and a Got it CTA.
  // Top-left spotlights the Publish button (still in the right pane toolbar).
  // Advances directly into the finale.
  {
    key: "deck-published",
    component: DeckPublishedStep,
    anchor: "topLeftCompact",
  },

  // Step 10: Make it yours — finale. Splash-anchored, hero card with
  // the three next-step directions (connect / teach / share). Calls
  // `onFinish` on its CTA, ending the sequence and unmounting the
  // widget. Bookends the opening welcome dialog visually so the user
  // exits onboarding on a high note.
  {
    key: "make-it-yours",
    component: MakeItYoursStep,
    anchor: "centerSplash",
  },
];
